import React, { useEffect, useState } from "react";
import { Button, Input, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import { ModalWindow } from "src/app/shared/components/modal-window/ModalWindow";
import { Autocomplete, TextField } from "@mui/material";
import "./ChannelOperatorsImportPopup.css";
import { IChannelOperatorsImportPopup } from "./~types/ChannelOperatorsImportPopup";

const ChannelOperatorsImportPopup = (props: IChannelOperatorsImportPopup): React.ReactElement => {
    const { t } = useTranslation();
    const [sourceChannelId, setSourceChannelId] = React.useState<number | "">("");
    const [keepSourceOperators, setKeepSourceOperators] = React.useState<boolean>(true);

    const _onKeepSourceOperatorsChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setKeepSourceOperators(event.currentTarget.checked);
    };

    const _onImportHandler = (): void => {
        if (sourceChannelId === "") 
            return;

        props.onImport(sourceChannelId,keepSourceOperators);
    };

    const _onCancelHandler = (): void => {
        props.onClose();
    };

    const _getHeader = (): React.ReactElement<{}> => {
        const currentChannel = props.channels.find((channel) =>channel.channelId === props.currentChannelId);
        return (
            <React.Fragment>
                <div className="channel-operators-import-title">{t("channels.importOperators")}</div>
                <div className="channel-operators-import__header-info">
                    {t("channels.currentChannel")}{" "}
                    <span className="channel-operators-import__header-bold">
                        #{currentChannel?.channelId}{" "}{currentChannel?.name}
                    </span>
                </div>
            </React.Fragment>
        );
    };

    const _getBody = (): React.ReactElement<{}> => {
        const availableChannels = props.channels
            .filter((channel) => channel.channelId !== props.currentChannelId && channel.operators.length > 0)
            .map((channel) => ({
                id: channel.channelId,
                label: `#${channel.channelId} ${channel.name}`,
            }));
        return (
            <React.Fragment>
                <Autocomplete
                    disablePortal
                    id="select-source-channel"
                    options={availableChannels}
                    classes={{input: "select-source-input", option: "select-source-option"}}
                    onChange={(event, newValue) => {
                        setSourceChannelId(newValue?.id ?? "");
                    }}
                    renderInput={(params) => (<TextField {...params} label={t("channels.selectSourceChannel")}/>)}
                    noOptionsText={t("channels.noOptionsText")}
                />

                <div className="channel-operators-import__keep-source">
                    <Label check className="channel-operators-import__checkbox-label">
                        <Input type="checkbox" checked={keepSourceOperators} onChange={_onKeepSourceOperatorsChange}/>
                        <span>{t("channels.keepOperatorsInSourceChannel")}</span>
                    </Label>
                </div>
            </React.Fragment>
        );
    };

    const _getFooter = (): React.ReactElement<{}> => {
        return (
            <React.Fragment>
                <Button className="channel-operators-import__button channel-operators-import__button-apply" onClick={_onImportHandler} disabled={sourceChannelId === ""}>
                    {t("common.import")}
                </Button>
                <Button className="channel-operators-import__button channel-operators-import__button-cancel" onClick={_onCancelHandler}>
                    {t("common.cancel")}
                </Button>
            </React.Fragment>
        );
    };

    return (
        <ModalWindow
            isOpen={props.isOpen}
            className="channel-operators-import"
            body={_getBody()}
            header={_getHeader()}
            footer={_getFooter()}
        />
    );
};

export default ChannelOperatorsImportPopup;